import { useState, useEffect, useRef, type MouseEvent as ReactMouseEvent } from 'react'
import type { OrgUser, UserRole, Clinic, Invite } from '../types'
import { createInvite, revokeInvite, updateUserAccess, deleteUser } from '../services/users'
import { isValidEmail } from '../utils/validators'
import { buildInviteRows, inviteExpiryLabel, type InviteRowState } from '../utils/inviteRows'
import EmailInput from './EmailInput'

/** Hover tooltip describing each role — shown inline next to the Role label. */
function RoleTooltip() {
  return (
    <span className="role-tooltip">
      <span className="role-tooltip__icon" aria-label="Role descriptions">ℹ</span>
      <span className="role-tooltip__content" role="tooltip">
        <span className="role-tooltip__row">
          <strong>Admin</strong>
          <span>Access to all clinics. Can manage users, clinics, and settings.</span>
        </span>
        <span className="role-tooltip__row">
          <strong>Manager</strong>
          <span>Manages one clinic. Can manage patients and providers.</span>
        </span>
        <span className="role-tooltip__row">
          <strong>User</strong>
          <span>Works within one clinic. Can manage patients and run eligibility checks.</span>
        </span>
      </span>
    </span>
  )
}

interface UsersListProps {
  users: OrgUser[]
  invites: Invite[]
  clinics: Clinic[]
  loading: boolean
  error: string | null
  currentUid: string
  onRefresh: () => void
}

const ROLE_LABELS: Record<UserRole, string> = {
  admin: 'Admin',
  manager: 'Manager',
  user: 'Staff',
}

function getClinicName(clinics: Clinic[], clinicId: string | null): string {
  if (!clinicId) return 'All Clinics'
  return clinics.find(c => c.id === clinicId)?.name ?? '—'
}

type SortCol = 'name' | 'email' | 'clinic' | 'role'
type SortDir = 'asc' | 'desc'

function ColTh({
  label, col, sortBy, sortDir, onSort,
}: {
  label: string; col: SortCol; sortBy: SortCol; sortDir: SortDir; onSort: (c: SortCol) => void
}) {
  const active = sortBy === col
  return (
    <th
      className={`clinics-table__th sortable-th${active ? ' sortable-th--active' : ''}`}
      onClick={() => onSort(col)}
    >
      {label}
      {active && <span className="sort-arrow">{sortDir === 'asc' ? '↑' : '↓'}</span>}
    </th>
  )
}

function displayName(user: OrgUser): string {
  return user.displayName || user.email || user.uid
}

/** Unified table row — a real user, or the newest invite for an email that has
 *  no user yet, live or expired (see `buildInviteRows`). */
type Row =
  | { kind: 'user'; user: OrgUser }
  | { kind: 'invite'; invite: Invite; state: InviteRowState }

function rowSortKey(row: Row, col: SortCol, clinics: Clinic[]): string {
  if (row.kind === 'user') {
    const u = row.user
    if (col === 'name')   return displayName(u)
    if (col === 'email')  return u.email ?? ''
    if (col === 'clinic') return getClinicName(clinics, u.clinicId)
    if (col === 'role')   return u.role
  } else {
    const inv = row.invite
    if (col === 'name')   return inv.email
    if (col === 'email')  return inv.email
    if (col === 'clinic') return getClinicName(clinics, inv.clinicId)
    if (col === 'role')   return inv.role
  }
  return ''
}

function rowMatchesSearch(row: Row, q: string): boolean {
  const lq = q.toLowerCase()
  if (row.kind === 'user') {
    const u = row.user
    return (u.displayName?.toLowerCase().includes(lq) ?? false)
      || (u.email?.toLowerCase().includes(lq) ?? false)
  }
  return row.invite.email.toLowerCase().includes(lq)
}

function rowMatchesClinic(row: Row, filterClinicId: string): boolean {
  if (filterClinicId === 'all') return true
  const clinicId = row.kind === 'user' ? row.user.clinicId : row.invite.clinicId
  return clinicId === filterClinicId
}

export default function UsersList({ users, invites, clinics, loading, error, currentUid, onRefresh }: UsersListProps) {
  // Sort state
  const [sortBy, setSortBy] = useState<SortCol>('name')
  const [sortDir, setSortDir] = useState<SortDir>('asc')

  function handleSort(col: SortCol) {
    if (col === sortBy) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortBy(col); setSortDir('asc') }
  }

  // Invite modal state
  const [showInvite, setShowInvite] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<UserRole>('user')
  const [inviteClinicId, setInviteClinicId] = useState<string | null>(null)
  const [inviteAttempted, setInviteAttempted] = useState(false)
  const [inviteSaving, setInviteSaving] = useState(false)
  const [inviteError, setInviteError] = useState<string | null>(null)
  const [inviteLink, setInviteLink] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  // Edit modal state
  const [editUser, setEditUser] = useState<OrgUser | null>(null)
  const [editRole, setEditRole] = useState<UserRole>('user')
  const [editClinicId, setEditClinicId] = useState<string | null>(null)
  const [editSaving, setEditSaving] = useState(false)
  const [editError, setEditError] = useState<string | null>(null)

  // Action menu state. The menu is position:fixed (anchored to the trigger's
  // viewport rect) so the table card's overflow:hidden can't clip it.
  const [openMenuId, setOpenMenuId] = useState<string | null>(null)
  const [menuPos, setMenuPos] = useState<{ top?: number; bottom?: number; right: number } | null>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  function toggleMenu(rowId: string, e: ReactMouseEvent<HTMLButtonElement>) {
    if (openMenuId === rowId) {
      setOpenMenuId(null)
      return
    }
    const rect = e.currentTarget.getBoundingClientRect()
    const right = window.innerWidth - rect.right
    // Open upward when there's no room below (anchor by bottom so any menu height fits)
    const openUp = rect.bottom + 4 + 96 > window.innerHeight
    setMenuPos(openUp
      ? { bottom: window.innerHeight - rect.top + 4, right }
      : { top: rect.bottom + 4, right })
    setOpenMenuId(rowId)
  }

  // Inline action feedback
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  // Toolbar state
  const [search, setSearch] = useState('')
  const [filterClinicId, setFilterClinicId] = useState<string>('all')

  // Close action menu on outside click
  useEffect(() => {
    if (!openMenuId) return
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenuId(null)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [openMenuId])

  // A fixed-position menu doesn't follow its anchor — close it on scroll/resize
  useEffect(() => {
    if (!openMenuId) return
    const close = () => setOpenMenuId(null)
    window.addEventListener('scroll', close, true)
    window.addEventListener('resize', close)
    return () => {
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('resize', close)
    }
  }, [openMenuId])

  // Build unified, sorted, filtered row list. Invites: one row per email, the
  // newest, read as Invited or Expired by its date — an expired invite stays
  // visible so the admin sees what became of the link they created and can
  // make a new one from the row, or remove it (revoke under the hood).
  const rows: Row[] = [
    ...users.map(u => ({ kind: 'user' as const, user: u })),
    ...buildInviteRows(invites, users).map(r => ({ kind: 'invite' as const, invite: r.invite, state: r.state })),
  ]

  const filtered = rows
    .filter(row => rowMatchesClinic(row, filterClinicId))
    .filter(row => !search || rowMatchesSearch(row, search))
    .sort((a, b) => {
      const mult = sortDir === 'asc' ? 1 : -1
      const va = rowSortKey(a, sortBy, clinics)
      const vb = rowSortKey(b, sortBy, clinics)
      return mult * va.localeCompare(vb, undefined, { sensitivity: 'base' })
    })

  function openInvite() {
    setInviteEmail('')
    setInviteRole('user')
    setInviteClinicId(clinics[0]?.id ?? null)
    setInviteAttempted(false)
    setInviteError(null)
    setInviteLink(null)
    setCopied(false)
    setShowInvite(true)
  }

  function closeInvite() {
    setShowInvite(false)
    setInviteLink(null)
    setCopied(false)
    setInviteError(null)
  }

  function openEdit(user: OrgUser) {
    setEditUser(user)
    setEditRole(user.role)
    setEditClinicId(user.clinicId)
    setEditError(null)
    setEditSaving(false)
  }

  function closeEdit() {
    setEditUser(null)
    setEditError(null)
  }

  async function handleInviteSubmit() {
    setInviteAttempted(true)
    if (!isValidEmail(inviteEmail)) return
    if (inviteRole !== 'admin' && !inviteClinicId) return

    setInviteSaving(true)
    setInviteError(null)
    try {
      const { token } = await createInvite({
        email: inviteEmail.toLowerCase().trim(),
        role: inviteRole,
        clinicId: inviteRole === 'admin' ? null : inviteClinicId,
      })
      const url = `${window.location.origin}/accept-invite?token=${token}`
      setInviteLink(url)
      // Auto-copy — best effort, fails in non-secure contexts; link is shown inline so user can copy manually
      navigator.clipboard.writeText(url).then(() => setCopied(true)).catch(() => {})
      onRefresh()
    } catch (err) {
      setInviteError(err instanceof Error ? err.message : 'Failed to create invite.')
    } finally {
      setInviteSaving(false)
    }
  }

  async function handleEditSave() {
    if (!editUser) return
    setEditSaving(true)
    setEditError(null)
    try {
      await updateUserAccess({
        targetUid: editUser.uid,
        role: editRole,
        clinicId: editRole === 'admin' ? null : editClinicId,
      })
      closeEdit()
      onRefresh()
    } catch (err) {
      setEditError(err instanceof Error ? err.message : 'Failed to save changes.')
    } finally {
      setEditSaving(false)
    }
  }

  async function handleResend(invite: Invite, state: InviteRowState) {
    setOpenMenuId(null)
    setActionLoading(invite.id)
    setActionError(null)
    try {
      // A live invite is revoked first so its old link stops working. An
      // expired one already doesn't work and no longer counts as a duplicate
      // on the backend, so only the fresh invite is created.
      if (state === 'invited') {
        await revokeInvite(invite.id)
      }

      const { token } = await createInvite({
        email: invite.email,
        role: invite.role,
        clinicId: invite.clinicId,
      })
      const url = `${window.location.origin}/accept-invite?token=${token}`
      // Show the fresh link in the invite modal — the token is stored hashed,
      // so this render is the only chance to ever see it.
      setInviteEmail(invite.email)
      setInviteAttempted(false)
      setInviteError(null)
      setCopied(false)
      setInviteLink(url)
      setShowInvite(true)
      navigator.clipboard.writeText(url).then(() => setCopied(true)).catch(() => {})
      onRefresh()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to resend invite.')
    } finally {
      setActionLoading(null)
    }
  }

  async function handleRevoke(inviteId: string) {
    setOpenMenuId(null)
    setActionLoading(inviteId)
    setActionError(null)
    try {
      await revokeInvite(inviteId)
      onRefresh()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to revoke invite.')
    } finally {
      setActionLoading(null)
    }
  }

  async function handleDeleteUser(uid: string) {
    setOpenMenuId(null)
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) return
    setActionLoading(uid)
    setActionError(null)
    try {
      await deleteUser(uid)
      onRefresh()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to delete user.')
    } finally {
      setActionLoading(null)
    }
  }

  const inviteErrors = {
    email: !isValidEmail(inviteEmail),
    clinicId: inviteRole !== 'admin' && !inviteClinicId,
  }
  function invInv(field: keyof typeof inviteErrors) {
    return inviteAttempted && inviteErrors[field] ? ' is-invalid' : ''
  }

  return (
    <>
      <div className="users-view">
        <div className="users-view__header">
          <div className="users-view__title-row">
            <svg className="users-view__icon" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
            <h2 className="users-view__title">Users</h2>
          </div>
          <div className="users-view__header-right">
            <select
              className="toolbar-select"
              value={filterClinicId}
              onChange={e => setFilterClinicId(e.target.value)}
              aria-label="Filter by clinic"
            >
              <option value="all">All Clinics</option>
              {clinics.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <input
              type="search"
              className="users-view__search"
              placeholder="Search..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <button className="create-doctor-btn create-doctor-btn--primary" onClick={openInvite}>
              + Invite User
            </button>
          </div>
        </div>

        {/* Load error banner */}
        {error && (
          <div className="users-view__error-banner">
            {error}
            <button className="users-view__error-retry" onClick={onRefresh}>Retry</button>
          </div>
        )}

        {/* Inline action error */}
        {actionError && (
          <div className="users-view__error-banner">
            {actionError}
            <button className="users-view__error-retry" onClick={() => setActionError(null)}>Dismiss</button>
          </div>
        )}

        <div className="clinics-table-wrap">
          {loading ? (
            <p className="clinics-view__empty">Loading users...</p>
          ) : filtered.length === 0 ? (
            <p className="clinics-view__empty">
              {search ? 'No users match your search.' : 'No users found.'}
            </p>
          ) : (
            <table className="clinics-table">
              <colgroup>
                <col style={{ width: '28%' }} />
                <col style={{ width: '30%' }} />
                <col style={{ width: '18%' }} />
                <col style={{ width: '14%' }} />
                <col style={{ width: '10%' }} />
              </colgroup>
              <thead>
                <tr className="clinics-table__head-row">
                  <ColTh label="Name"   col="name"   sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                  <ColTh label="Email"  col="email"  sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                  <ColTh label="Clinic" col="clinic" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                  <ColTh label="Role"   col="role"   sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                  <th className="clinics-table__th" />
                </tr>
              </thead>
              <tbody>
                {filtered.map(row => {
                  if (row.kind === 'user') {
                    const u = row.user
                    const isCurrentUser = u.uid === currentUid
                    const rowId = `user-${u.uid}`
                    return (
                      <tr
                        key={rowId}
                        className="clinics-table__row"
                        onClick={() => openEdit(u)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openEdit(u) } }}
                      >
                        <td className="clinics-table__cell clinics-table__cell--name">
                          <span className="invited-name-row">
                            <span className="invited-name-row__text" title={displayName(u)}>{displayName(u)}</span>
                            {isCurrentUser && <span className="you-badge">You</span>}
                          </span>
                        </td>
                        <td className="clinics-table__cell clinics-table__cell--email" title={u.email ?? undefined}>
                          {u.email ?? <span className="clinics-table__empty-cell">—</span>}
                        </td>
                        <td className="clinics-table__cell">
                          {getClinicName(clinics, u.clinicId)}
                        </td>
                        <td className="clinics-table__cell">
                          <span className={`user-role-badge user-role-badge--${u.role}`}>
                            {ROLE_LABELS[u.role] ?? u.role}
                          </span>
                        </td>
                        <td className="clinics-table__cell users-action-cell" onClick={e => e.stopPropagation()}>
                          {!isCurrentUser && (
                            <div className="users-action-wrap" ref={openMenuId === rowId ? menuRef : undefined}>
                              <button
                                className="users-action-btn"
                                aria-label="Actions"
                                disabled={actionLoading === u.uid}
                                onClick={e => toggleMenu(rowId, e)}
                              >
                                {actionLoading === u.uid ? <span className="users-action-spinner" /> : '⋯'}
                              </button>
                              {openMenuId === rowId && (
                                <div className="users-action-menu" style={menuPos ?? undefined}>
                                  <button
                                    className="users-action-menu__item users-action-menu__item--danger"
                                    onClick={() => handleDeleteUser(u.uid)}
                                  >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                    </svg>
                                    Delete user
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    )
                  }

                  // Invite row — live ("Invited") or expired; the badge's hover shows the date
                  const inv = row.invite
                  const expired = row.state === 'expired'
                  const rowId = `invite-${inv.id}`
                  const expiryLabel = inviteExpiryLabel(inv, row.state)
                  return (
                    <tr key={rowId} className="clinics-table__row clinics-table__row--no-click">
                      <td className="clinics-table__cell clinics-table__cell--name">
                        <span className="invited-name-row">
                          <span className="invited-name-row__text" title={inv.email}>{inv.email}</span>
                          <span
                            className={`invited-badge${expired ? ' invited-badge--expired' : ''}`}
                            title={expiryLabel ?? undefined}
                          >
                            {expired ? 'Expired' : 'Invited'}
                          </span>
                        </span>
                      </td>
                      <td className="clinics-table__cell clinics-table__cell--email" title={inv.email}>
                        {inv.email}
                      </td>
                      <td className="clinics-table__cell">
                        {getClinicName(clinics, inv.clinicId)}
                      </td>
                      <td className="clinics-table__cell">
                        <span className={`user-role-badge user-role-badge--${inv.role}`}>
                          {ROLE_LABELS[inv.role] ?? inv.role}
                        </span>
                      </td>
                      <td className="clinics-table__cell users-action-cell">
                        <div className="users-action-wrap" ref={openMenuId === rowId ? menuRef : undefined}>
                          <button
                            className="users-action-btn"
                            aria-label="Actions"
                            disabled={actionLoading === inv.id}
                            onClick={e => toggleMenu(rowId, e)}
                          >
                            {actionLoading === inv.id ? <span className="users-action-spinner" /> : '⋯'}
                          </button>
                          {openMenuId === rowId && (
                            <div className="users-action-menu" style={menuPos ?? undefined}>
                              <button
                                className="users-action-menu__item"
                                onClick={() => handleResend(inv, row.state)}
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M22 2 11 13M22 2l-7 20-4-9-9-4 20-7z" />
                                </svg>
                                Create new invite link
                              </button>
                              <button
                                className="users-action-menu__item users-action-menu__item--danger"
                                onClick={() => handleRevoke(inv.id)}
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                  <circle cx="8.5" cy="7" r="4" />
                                  <line x1="18" y1="8" x2="23" y2="13" />
                                  <line x1="23" y1="8" x2="18" y2="13" />
                                </svg>
                                {expired ? 'Remove invite' : 'Revoke invite'}
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Invite User Modal */}
      {showInvite && (
        <div className="doctor-modal-overlay" onClick={closeInvite}>
          <div className="doctor-modal" onClick={e => e.stopPropagation()}>
            <div className="doctor-modal__header">
              <h3 className="doctor-modal__title">Invite User</h3>
              <button className="doctor-modal__close" onClick={closeInvite} aria-label="Close">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="add-doctor-form">
              {inviteLink ? (
                <div className="invite-link-panel">
                  <p className="invite-link-panel__intro">
                    Invite created for <strong>{inviteEmail.toLowerCase().trim()}</strong>. Copy the link below and send it to them directly.
                  </p>
                  <div className="invite-link-panel__row">
                    <input
                      className="invite-link-panel__input"
                      type="text"
                      readOnly
                      value={inviteLink}
                      onFocus={e => e.target.select()}
                    />
                    <button
                      className="invite-link-panel__copy-btn"
                      onClick={() => {
                        navigator.clipboard.writeText(inviteLink).then(() => {
                          setCopied(true)
                          setTimeout(() => setCopied(false), 2000)
                        })
                      }}
                    >
                      {copied ? '✓ Copied!' : 'Copy Link'}
                    </button>
                  </div>
                  <div className="add-doctor-form__actions">
                    <span />
                    <button className="check-btn add-doctor-form__submit" onClick={closeInvite}>Done</button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="add-doctor-form__fields">
                    <div className="form-field form-field--full">
                      <label className="form-field__label">Email *</label>
                      <EmailInput
                        className={`form-field__input${invInv('email')}`}
                        value={inviteEmail}
                        onValueChange={setInviteEmail}
                        placeholder="dr.smith@clinic.com"
                        autoFocus
                      />
                    </div>

                    <div className="form-field">
                      <label className="form-field__label">Role * <RoleTooltip /></label>
                      <select
                        className="form-field__input"
                        value={inviteRole}
                        onChange={e => {
                          const r = e.target.value as UserRole
                          setInviteRole(r)
                          if (r === 'admin') setInviteClinicId(null)
                          else if (!inviteClinicId) setInviteClinicId(clinics[0]?.id ?? null)
                        }}
                      >
                        <option value="user">Staff</option>
                        <option value="manager">Manager</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>

                    {inviteRole !== 'admin' && (
                      <div className="form-field">
                        <label className="form-field__label">Clinic *</label>
                        <select
                          className={`form-field__input${invInv('clinicId')}`}
                          value={inviteClinicId ?? ''}
                          onChange={e => setInviteClinicId(e.target.value || null)}
                        >
                          <option value="">Select clinic...</option>
                          {clinics.map(c => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>

                  {inviteError && <p className="clinic-save-error">{inviteError}</p>}

                  <div className="add-doctor-form__actions">
                    <span />
                    <div className="add-doctor-form__actions-right">
                      <button className="add-doctor-form__cancel" onClick={closeInvite}>Cancel</button>
                      <button
                        className="check-btn add-doctor-form__submit"
                        disabled={inviteSaving}
                        onClick={handleInviteSubmit}
                      >
                        {inviteSaving ? 'Creating...' : 'Create Invite'}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editUser && (
        <div className="doctor-modal-overlay" onClick={closeEdit}>
          <div className="doctor-modal" onClick={e => e.stopPropagation()}>
            <div className="doctor-modal__header">
              <h3 className="doctor-modal__title">Edit User</h3>
              <button className="doctor-modal__close" onClick={closeEdit} aria-label="Close">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="add-doctor-form">
              <div className="add-doctor-form__fields">
                <div className="form-field form-field--full">
                  <label className="form-field__label">Name</label>
                  <input
                    className="form-field__input"
                    value={displayName(editUser)}
                    readOnly
                    disabled
                  />
                </div>

                <div className="form-field form-field--full">
                  <label className="form-field__label">Email</label>
                  <input
                    className="form-field__input"
                    value={editUser.email ?? ''}
                    readOnly
                    disabled
                  />
                </div>

                <div className="form-field">
                  <label className="form-field__label">Role *</label>
                  <select
                    className="form-field__input"
                    value={editRole}
                    onChange={e => {
                      const r = e.target.value as UserRole
                      setEditRole(r)
                      if (r === 'admin') setEditClinicId(null)
                      else if (!editClinicId) setEditClinicId(clinics[0]?.id ?? null)
                    }}
                  >
                    <option value="user">Staff</option>
                    <option value="manager">Manager</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>

                {editRole !== 'admin' && (
                  <div className="form-field">
                    <label className="form-field__label">Clinic *</label>
                    <select
                      className="form-field__input"
                      value={editClinicId ?? ''}
                      onChange={e => setEditClinicId(e.target.value || null)}
                    >
                      <option value="">Select clinic...</option>
                      {clinics.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {editError && <p className="clinic-save-error">{editError}</p>}

              <div className="add-doctor-form__actions">
                <span />
                <div className="add-doctor-form__actions-right">
                  <button className="add-doctor-form__cancel" onClick={closeEdit}>Cancel</button>
                  <button
                    className="check-btn add-doctor-form__submit"
                    disabled={editSaving}
                    onClick={handleEditSave}
                  >
                    {editSaving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
